import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import useToggle from 'components/hooks/useToggle';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrackedPersonnelContext } from 'components/contexts/TrackedPersonnelContext';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import ELearningBookings from 'components/training/ELearningBookings';
import MinimalTrainingOptions from 'components/training/MinimalTrainingOptions';
import AddRequirementPriorityModal from 'components/training/requirementPriorityModals/AddRequirementPriorityModal';
import RemoveRequirementPriorityModal from 'components/training/requirementPriorityModals/RemoveRequirementPriorityModal';
import UpdateRequirementPriorityModal from 'components/training/requirementPriorityModals/UpdateRequirementPriorityModal';
import TrainingBookings from 'components/training/TrainingBookings';
import TrainingOptions from 'components/training/TrainingOptions';
import TrainingRow from 'components/training/TrainingRow';

import { groupAttachmentsByAttachableId } from 'components/helpers/resources/attachments';
import { prioritisedAutoEnrolmentBlockers as prioritisedAutoEnrolmentBlockersList } from 'components/helpers/resources/autoEnrolments';
import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function CourseTraining({
  attachments,
  autoEnrolmentConfiguration,
  autoEnrolmentExclusion,
  booker,
  booking,
  course,
  creditInfoUnavailable,
  currentPersonnel,
  eLearningAllowance,
  eLearningCourse,
  hasTrainingEditableAccess,
  isAdditional,
  isAssignedToAnotherRole,
  isOptionalOnAnotherRole,
  isRequired,
  isRequiredByAnything,
  isRequiredByCourseCompanyRole,
  isRequiredByUserCourse,
  isWithinRoleTable,
  onAddBooking,
  onAddELearningBooking,
  onBookingReminderClick,
  onCreateAutoEnrolmentExclusion,
  onDeleteAutoEnrolmentExclusion,
  onDeleteCourseClick,
  onDeleteTrainingClick,
  onEditTrainingClick,
  onNewTrainingClick,
  onRemoveBooking,
  onShowBooking,
  onUserCourseCreate,
  onUserCourseUpdate,
  personnelCourseConnectionRequirementPriority,
  personnelCourseConnectionRequirementPriorityUser,
  personnelId,
  registration,
  requiredSource,
  setModalBoxData,
  training,
  trainingStatus,
  userCourse,
}) {
  const currentActor = useCurrentActor();

  const { hasReachedLimit, isCurrentPersonnelTracked } =
    useTrackedPersonnelContext();

  const {
    hasBookingEditableAccess,
    isAutoEnrolmentIncludingMissingTraining,
    isViewingOwnProfile,
  } = useTrainingRegisterResources();

  const attachmentsByAttachableId = groupAttachmentsByAttachableId({
    attachments,
  });
  const isArchived = !!currentPersonnel.archivedAt;
  const isReadOnlyOrArchived = !hasTrainingEditableAccess || isArchived;
  const courseHasELearningCourse =
    currentActor.isAllowedFeature('e_learning') && !!eLearningCourse;
  const isELearningNonCustom =
    courseHasELearningCourse && !eLearningCourse.attributes.custom;
  const latestTraining = training[0];
  const isBookingAllowedWithinTrackedPersonnelLimits =
    !hasReachedLimit || isCurrentPersonnelTracked;

  const [isCurrentlyRequired, setIsCurrentlyRequired] = useState(isRequired);

  const [
    currentAutoEnrolmentExclusionUserSelection,
    setCurrentAutoEnrolmentExclusionUserSelection,
  ] = useState(!!autoEnrolmentExclusion);
  const [isHistoricalTrainingVisible, toggleHistoricalTraining] =
    useToggle(false);
  const [
    isAddRequirementPriorityModalOpen,
    setIsAddRequirementPriorityModalOpen,
  ] = useState(false);
  const [
    isRemoveRequirementPriorityModalOpen,
    setIsRemoveRequirementPriorityModalOpen,
  ] = useState(false);
  const [
    isUpdateRequirementPriorityModalOpen,
    setIsUpdateRequirementPriorityModalOpen,
  ] = useState(false);

  const handleRequirementChange = (newRequirementSelection) => {
    if (isWithinRoleTable) {
      handleRequirementChangeWithinRoleTable();
    } else {
      handleRequirementChangeOutsideRoleTable(newRequirementSelection);
    }
  };

  const handleRequirementChangeWithinRoleTable = () => {
    const requirementPriorityExists =
      !!personnelCourseConnectionRequirementPriority;
    const userCourseExists = !!userCourse;

    if (!userCourseExists) return setIsAddRequirementPriorityModalOpen(true);

    if (!requirementPriorityExists) {
      if (isRequiredByUserCourse && !isRequiredByCourseCompanyRole) {
        // align user course requirement with role requirement.
        // no need to add or remove an override
        setIsCurrentlyRequired(isRequiredByCourseCompanyRole);
        onUserCourseUpdate({
          userCourse,
          params: { required: isRequiredByCourseCompanyRole },
        });
      } else {
        setIsAddRequirementPriorityModalOpen(true);
      }
      return;
    }

    const overrideStatus =
      isRequiredByCourseCompanyRole === isRequiredByUserCourse ? 'inactive' : (
        'active'
      );

    return overrideStatus === 'active' ?
        setIsRemoveRequirementPriorityModalOpen(true)
      : setIsUpdateRequirementPriorityModalOpen(true);
  };

  const handleRequirementChangeOutsideRoleTable = (newRequirementSelection) => {
    if (!isAssignedToAnotherRole) {
      return setIsCurrentlyRequired(newRequirementSelection);
    }

    const requirementPriorityExists =
      !!personnelCourseConnectionRequirementPriority;
    const userCourseExists = !!userCourse;

    if (!userCourseExists) {
      if (isOptionalOnAnotherRole) {
        return setIsAddRequirementPriorityModalOpen(true);
      }

      setIsCurrentlyRequired(true);
      return onUserCourseCreate({ course, params: { required: true } });
    }

    if (!requirementPriorityExists) {
      if (isRequired) {
        setIsCurrentlyRequired(false);
        onUserCourseUpdate({ userCourse, params: { required: false } });
      } else if (isOptionalOnAnotherRole) {
        setIsAddRequirementPriorityModalOpen(true);
      } else {
        setIsCurrentlyRequired(true);
        onUserCourseUpdate({ userCourse, params: { required: true } });
      }
      return;
    }

    if (isRequired || !isOptionalOnAnotherRole) {
      return setIsRemoveRequirementPriorityModalOpen(true);
    }

    return setIsUpdateRequirementPriorityModalOpen(true);
  };

  const handleConfirmAddRequirementPriority = (reason) => {
    const newRequiredUserSelection =
      isWithinRoleTable ? !isRequiredByCourseCompanyRole : !isRequired;
    setIsCurrentlyRequired(newRequiredUserSelection);

    if (!userCourse) {
      onUserCourseCreate({
        course,
        params: {
          required: newRequiredUserSelection,
          personnel_course_connection_requirement_priority_attributes: {
            reason,
          },
        },
      });
    } else {
      onUserCourseUpdate({
        userCourse,
        params: {
          required: newRequiredUserSelection,
          personnel_course_connection_requirement_priority_attributes: {
            reason,
          },
        },
      });
    }

    setIsAddRequirementPriorityModalOpen(false);
  };

  const handleConfirmUpdateRequirementPriority = (reason) => {
    const newRequiredUserSelection =
      isWithinRoleTable ? !isRequiredByCourseCompanyRole : !isRequired;

    setIsCurrentlyRequired(newRequiredUserSelection);
    onUserCourseUpdate({
      userCourse,
      params: {
        required: newRequiredUserSelection,
        personnel_course_connection_requirement_priority_attributes: {
          id: personnelCourseConnectionRequirementPriority.id,
          reason,
        },
      },
    });
    setIsUpdateRequirementPriorityModalOpen(false);
  };

  const handleConfirmRemoveRequirementPriority = () => {
    const newRequiredUserSelection =
      isWithinRoleTable ? isRequiredByCourseCompanyRole : !isRequired;
    setIsCurrentlyRequired(newRequiredUserSelection);
    onUserCourseUpdate({
      userCourse,
      params: {
        required: newRequiredUserSelection,
        personnel_course_connection_requirement_priority_attributes: {
          id: personnelCourseConnectionRequirementPriority.id,
          _destroy: true,
        },
      },
    });
    setIsRemoveRequirementPriorityModalOpen(false);
  };

  const handleCurrentAutoEnrolmentExclusionUserSelectionChange = (
    currentAutoEnrolmentExclusionUserSelection,
  ) =>
    setCurrentAutoEnrolmentExclusionUserSelection(
      currentAutoEnrolmentExclusionUserSelection,
    );
  const isEligibleForAutoEnrolment =
    eLearningCourse &&
    currentActor.isAllowedFeature([
      'e_learning_auto_enrol',
      'e_learning',
      'training_register',
    ]);

  const prioritisedAutoEnrolmentBlockers =
    isEligibleForAutoEnrolment ?
      prioritisedAutoEnrolmentBlockersList({
        autoEnrolmentConfiguration,
        isExpiringCourse: course.attributes.expires,
        training: latestTraining,
        isSubcontractor: !!currentPersonnel.company.subcontractorId,
        isRequired: isCurrentlyRequired,
        isAutoEnrolmentIncludingMissingTraining,
        isBookingAllowedWithinTrackedPersonnelLimits,
        isELearningNonCustom,
        isCreditInfoUnavailable: creditInfoUnavailable,
        isPersonnelEmailUnavailable: !currentPersonnel.email,
        currentELearningCredits: eLearningAllowance?.currentCredits,
        isFromAutoEnrollableDivision:
          currentPersonnel.isFromAutoEnrollableDivision,
      })
    : null;

  const handleTrainingOptionsClose = () => {
    manageUserCourses();
    if (isEligibleForAutoEnrolment) {
      manageAutoEnrolmentExclusions();
    }
  };

  const manageUserCourses = () => {
    if (isWithinRoleTable || isAssignedToAnotherRole) return;

    if (!userCourse && isCurrentlyRequired) {
      onUserCourseCreate({ course, params: { required: true } });
    } else if (
      userCourse &&
      userCourse.attributes.required !== isCurrentlyRequired
    ) {
      onUserCourseUpdate({
        userCourse,
        params: { required: isCurrentlyRequired },
      });
    }
  };

  const manageAutoEnrolmentExclusions = () => {
    if (prioritisedAutoEnrolmentBlockers.length === 0) {
      if (
        !currentAutoEnrolmentExclusionUserSelection &&
        autoEnrolmentExclusion
      ) {
        onDeleteAutoEnrolmentExclusion({ autoEnrolmentExclusion });
      } else if (
        currentAutoEnrolmentExclusionUserSelection &&
        !autoEnrolmentExclusion
      ) {
        onCreateAutoEnrolmentExclusion({
          autoEnrolmentConfigurationId: autoEnrolmentConfiguration.id,
          personnelId: currentPersonnel.id,
        });
      }
    }
  };

  const isOverrideActive = (() => {
    if (isWithinRoleTable) {
      return (
        personnelCourseConnectionRequirementPriority &&
        isRequiredByUserCourse !== isRequiredByCourseCompanyRole
      );
    } else if (isAssignedToAnotherRole) {
      return personnelCourseConnectionRequirementPriority && isRequired;
    } else {
      return false;
    }
  })();

  const renderBookings = ({ currentTrainingStatus }) => {
    if (isArchived) return null;

    const isEligibleForManualBooking =
      (isRequired || isRequiredByAnything) &&
      isBookingAllowedWithinTrackedPersonnelLimits &&
      hasTrainingEditableAccess &&
      !['valid', 'expiryDateMissing'].includes(currentTrainingStatus);

    return courseHasELearningCourse ?
        <ELearningBookings
          autoEnrolmentConfiguration={autoEnrolmentConfiguration}
          booker={booker}
          booking={booking}
          course={course}
          currentAutoEnrolmentExclusionUserSelection={
            currentAutoEnrolmentExclusionUserSelection
          }
          currentPersonnel={currentPersonnel}
          currentTrainingStatus={currentTrainingStatus}
          eLearningCourse={eLearningCourse}
          isCreditInfoUnavailable={
            isELearningNonCustom && creditInfoUnavailable
          }
          isELearningLaunchable={isViewingOwnProfile}
          isEligibleForManualBooking={
            isEligibleForManualBooking && hasBookingEditableAccess
          }
          latestTraining={latestTraining}
          onAddELearningBooking={onAddELearningBooking}
          prioritisedAutoEnrolmentBlockers={prioritisedAutoEnrolmentBlockers}
          registration={registration}
        />
      : <TrainingBookings
          booking={booking}
          course={course}
          currentTrainingStatus={currentTrainingStatus}
          isBookingTooltipDisplayable={
            hasBookingEditableAccess || hasTrainingEditableAccess
          }
          isEligibleForManualBooking={
            isEligibleForManualBooking && hasBookingEditableAccess
          }
          onAddBooking={onAddBooking}
          onShowBooking={onShowBooking}
        />;
  };

  const renderRegularTrainingActions = ({ training: optionsTraining }) => {
    if (isReadOnlyOrArchived && training.length <= 1) return null;

    return (
      <TrainingOptions
        booking={booking}
        course={course}
        courseHasELearningCourse={courseHasELearningCourse}
        creditInfoUnavailable={isELearningNonCustom && creditInfoUnavailable}
        currentAutoEnrolmentExclusionUserSelection={
          currentAutoEnrolmentExclusionUserSelection
        }
        currentPersonnel={currentPersonnel}
        eLearningCourse={eLearningCourse}
        isAdditional={isAdditional}
        isAssignedToAnotherRole={isAssignedToAnotherRole}
        isCurrentlyRequired={isCurrentlyRequired}
        isHistoricalTrainingVisible={isHistoricalTrainingVisible}
        isMultipleTraining={training.length > 1}
        isWithinRoleTable={isWithinRoleTable}
        onAddBooking={onAddBooking}
        onAddELearningBooking={onAddELearningBooking}
        onBookingReminderClick={onBookingReminderClick}
        onCurrentAutoEnrolmentExclusionUserSelectionChange={
          handleCurrentAutoEnrolmentExclusionUserSelectionChange
        }
        onDeleteCourseClick={onDeleteCourseClick}
        onDeleteTrainingClick={onDeleteTrainingClick}
        onEditTrainingClick={onEditTrainingClick}
        onNewTrainingClick={onNewTrainingClick}
        onRemoveBooking={onRemoveBooking}
        onRequirementChange={handleRequirementChange}
        onTrainingOptionsClose={handleTrainingOptionsClose}
        personnelId={personnelId}
        prioritisedAutoEnrolmentBlockers={prioritisedAutoEnrolmentBlockers}
        registration={registration}
        showHistoricalTrainingOnly={isReadOnlyOrArchived}
        toggleHistoricalTraining={toggleHistoricalTraining}
        training={optionsTraining}
        userCourse={userCourse}
      />
    );
  };

  const renderHistoricalTrainingActions = ({ training }) => {
    if (isReadOnlyOrArchived) return null;
    return (
      <MinimalTrainingOptions
        onDeleteTrainingClick={() =>
          onDeleteTrainingClick({ training, course })
        }
        onEditTrainingClick={() => onEditTrainingClick({ training, course })}
      />
    );
  };

  useEffect(() => {
    if (training.length <= 1 && isHistoricalTrainingVisible)
      toggleHistoricalTraining(false);
  }, [JSON.stringify(training)]);

  return (
    <>
      <TrainingRow
        attachments={
          latestTraining && attachmentsByAttachableId[latestTraining.id]
        }
        course={course}
        hasTrainingEditableAccess={hasTrainingEditableAccess}
        isArchived={isArchived}
        isOverrideActive={isOverrideActive}
        isRequired={isRequired}
        renderActions={renderRegularTrainingActions}
        renderBookings={renderBookings}
        requiredSource={requiredSource}
        setModalBoxData={setModalBoxData}
        training={latestTraining}
        trainingStatus={isArchived ? 'archived' : trainingStatus}
      />
      {isHistoricalTrainingVisible &&
        training.length > 1 &&
        training
          .slice(1)
          .map((historicalTraining) => (
            <TrainingRow
              attachments={attachmentsByAttachableId[historicalTraining.id]}
              course={course}
              isArchived={isArchived}
              isHistorical={true}
              isRequired={isRequired}
              key={`historicalTraining--${historicalTraining.id}`}
              renderActions={renderHistoricalTrainingActions}
              setModalBoxData={setModalBoxData}
              training={historicalTraining}
            />
          ))}
      {isAddRequirementPriorityModalOpen && (
        <AddRequirementPriorityModal
          courseName={course.attributes.name}
          isRequired={isRequired}
          onClose={() => setIsAddRequirementPriorityModalOpen(false)}
          onConfirmAddRequirementPriority={handleConfirmAddRequirementPriority}
          personnelName={`${currentPersonnel.firstName} ${currentPersonnel.lastName}`}
        />
      )}
      {isRemoveRequirementPriorityModalOpen && (
        <RemoveRequirementPriorityModal
          courseName={course.attributes.name}
          isRequired={isRequired}
          onClose={() => setIsRemoveRequirementPriorityModalOpen(false)}
          onConfirmRemoveRequirementPriority={
            handleConfirmRemoveRequirementPriority
          }
          personnelName={personDisplayName({
            attributes: currentPersonnel,
          })}
          requirementPriorityReason={
            personnelCourseConnectionRequirementPriority.attributes.reason
          }
          requirementPriorityUserName={personDisplayName({
            attributes:
              personnelCourseConnectionRequirementPriorityUser.attributes,
          })}
        />
      )}
      {isUpdateRequirementPriorityModalOpen && (
        <UpdateRequirementPriorityModal
          courseName={course.attributes.name}
          isRequired={isRequired}
          onClose={() => setIsUpdateRequirementPriorityModalOpen(false)}
          onConfirmUpdateRequirementPriority={
            handleConfirmUpdateRequirementPriority
          }
          personnelName={personDisplayName({
            attributes: currentPersonnel,
          })}
          requirementPriorityReason={
            personnelCourseConnectionRequirementPriority.attributes.reason
          }
          requirementPriorityUserName={personDisplayName({
            attributes:
              personnelCourseConnectionRequirementPriorityUser.attributes,
          })}
        />
      )}
    </>
  );
}

CourseTraining.propTypes = {
  isRequired: PropTypes.bool,
  isWithinRoleTable: PropTypes.bool,
  course: resourceShape('course').isRequired,
  autoEnrolmentConfiguration: resourceShape('autoEnrolmentConfiguration'),
  autoEnrolmentExclusion: resourceShape('autoEnrolmentExclusion'),
  eLearningCourse: resourceShape('eLearningCourse'),
  eLearningAllowance: PropTypes.object,
  training: PropTypes.array,
  booker: PropTypes.object,
  booking: PropTypes.object,
  registration: PropTypes.object,
  attachments: PropTypes.array,
  creditInfoUnavailable: PropTypes.bool,
  onBookingReminderClick: PropTypes.func.isRequired,
  onShowBooking: PropTypes.func.isRequired,
  onRemoveBooking: PropTypes.func.isRequired,
  onCreateAutoEnrolmentExclusion: PropTypes.func,
  onDeleteAutoEnrolmentExclusion: PropTypes.func,
  currentPersonnel: PropTypes.shape({
    archivedAt: PropTypes.node,
    company: PropTypes.object,
    email: PropTypes.string,
    id: PropTypes.string,
  }).isRequired,
  personnelId: PropTypes.string,
  userCourse: resourceShape('userCourse'),
  hasTrainingEditableAccess: PropTypes.bool,
  isRequiredByAnything: PropTypes.bool,
  isAssignedToAnotherRole: PropTypes.bool,
  isOptionalOnAnotherRole: PropTypes.bool,
  requiredSource: PropTypes.string,
  isRequiredByCourseCompanyRole: PropTypes.bool,
  isRequiredByUserCourse: PropTypes.bool,
  isAdditional: PropTypes.bool,
  onAddBooking: PropTypes.func,
  onAddELearningBooking: PropTypes.func,
  onNewTrainingClick: PropTypes.func,
  onEditTrainingClick: PropTypes.func,
  onDeleteTrainingClick: PropTypes.func,
  onDeleteCourseClick: PropTypes.func,
  onUserCourseUpdate: PropTypes.func,
  onUserCourseCreate: PropTypes.func,
  setModalBoxData: PropTypes.func,
  personnelCourseConnectionRequirementPriority: resourceShape(
    'personnelCourseConnectionRequirementPriority',
  ),
  personnelCourseConnectionRequirementPriorityUser: resourceShape('simpleUser'),
};

CourseTraining.defaultProps = {
  hasTrainingEditableAccess: true,
  isRequired: true,
  isWithinRoleTable: true,
  attachments: [],
  training: [],
  booking: null,
  registration: null,
};
